import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  LinkedIn,
  Twitter,
  Instagram,
  Send,
  Close,
  Refresh,
} from '@mui/icons-material';
import { integrationsAPI, ConnectedAccount, PostRequest } from '../services/integrationsAPI';
import {
  IntegrationStatusCard,
  PlatformConfig,
  getConnectionState,
} from '../components/Integrations/IntegrationStatusCard';

const SUPPORTED_PLATFORMS = ['linkedin', 'x', 'instagram'] as const;

const platformConfigs: Record<string, PlatformConfig> = {
  linkedin: {
    name: 'LinkedIn',
    icon: <LinkedIn />,
    color: '#0077B5',
    description: 'Professional networking and business content',
  },
  x: {
    name: 'X (Twitter)',
    icon: <Twitter />,
    color: '#000000',
    description: 'Quick updates and real-time conversations',
  },
  instagram: {
    name: 'Instagram',
    icon: <Instagram />,
    color: '#E4405F',
    description: 'Visual content and stories',
  },
};

export const SocialIntegrationsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState('');

  const fetchConnectedAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await integrationsAPI.getConnectedAccounts();
      setConnectedAccounts(response.accounts || []);
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(
        apiErr.response?.data?.detail ||
          apiErr.message ||
          'Failed to fetch connected accounts'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnectedAccounts();
  }, [fetchConnectedAccounts]);

  const mergeStatusIntoAccounts = (status: ConnectedAccount) => {
    setConnectedAccounts((prev) =>
      prev.map((a) =>
        a.platform === status.platform
          ? {
              ...a,
              connected: status.connected,
              needs_reauth: status.needs_reauth,
              user_info: status.user_info ?? a.user_info,
              message: status.message ?? a.message,
              last_validated: new Date().toISOString(),
            }
          : a
      )
    );
  };

  const handleRefreshStatus = async (platform: string) => {
    setRefreshing(platform);
    setError(null);
    try {
      const status = await integrationsAPI.getPlatformStatus(platform);
      mergeStatusIntoAccounts({
        platform: status.platform as ConnectedAccount['platform'],
        connected: status.connected,
        needs_reauth: status.needs_reauth,
        user_info: status.user_info,
        message: status.message,
      });
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(apiErr.response?.data?.detail || apiErr.message || 'Failed to refresh status');
    } finally {
      setRefreshing(null);
    }
  };

  const handleConnect = async (platform: string) => {
    setConnecting(platform);
    setError(null);

    const authWindow = window.open(
      '',
      'oauth',
      'width=600,height=600,scrollbars=yes,resizable=yes'
    );

    if (!authWindow) {
      setConnecting(null);
      setError('Popup blocked. Allow popups for this site and try again.');
      return;
    }

    try {
      let authResponse;
      switch (platform) {
        case 'linkedin':
          authResponse = await integrationsAPI.getLinkedInAuthUrl();
          break;
        case 'x':
          authResponse = await integrationsAPI.getXAuthUrl();
          break;
        case 'instagram':
          authResponse = await integrationsAPI.getInstagramAuthUrl();
          break;
        default:
          throw new Error(`Unsupported platform: ${platform}`);
      }

      if (authResponse.setup_hint && platform === 'instagram') {
        setError(authResponse.setup_hint);
      }

      authWindow.location.href = authResponse.url;

      if (authWindow.closed) {
        window.location.href = authResponse.url;
        return;
      }

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data.type === 'oauth_success') {
          authWindow.close();
          window.removeEventListener('message', handleMessage);
          fetchConnectedAccounts();
          setConnecting(null);
        } else if (event.data.type === 'oauth_error') {
          authWindow.close();
          window.removeEventListener('message', handleMessage);
          setError(event.data.error || 'OAuth authentication failed');
          setConnecting(null);
        }
      };

      window.addEventListener('message', handleMessage);
      setTimeout(() => {
        setConnecting(null);
        window.removeEventListener('message', handleMessage);
      }, 60000);
    } catch (err: unknown) {
      authWindow.close();
      const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(
        apiErr.response?.data?.detail ||
          apiErr.message ||
          `Failed to connect to ${platform}`
      );
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform: string) => {
    setError(null);
    try {
      switch (platform) {
        case 'linkedin':
          await integrationsAPI.disconnectLinkedIn();
          break;
        case 'x':
          await integrationsAPI.disconnectX();
          break;
        case 'instagram':
          await integrationsAPI.disconnectInstagram();
          break;
      }
      fetchConnectedAccounts();
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(
        apiErr.response?.data?.detail ||
          apiErr.message ||
          `Failed to disconnect from ${platform}`
      );
    }
  };

  const handlePost = async () => {
    if (!postContent.trim()) {
      setError('Post content is required');
      return;
    }
    if (selectedPlatforms.length === 0) {
      setError('Please select at least one platform');
      return;
    }

    setPosting(true);
    setError(null);
    try {
      const postData: PostRequest = {
        content: postContent,
        platforms: selectedPlatforms,
        ...(imageUrl && { image_url: imageUrl }),
      };
      const response = await integrationsAPI.postToMultiplePlatforms(postData);
      const failedPosts = response.data.results.filter((r) => !r.success);
      if (failedPosts.length > 0) {
        setError(failedPosts.map((p) => `${p.platform}: ${p.error}`).join(', '));
      } else {
        setPostDialogOpen(false);
        setPostContent('');
        setSelectedPlatforms([]);
        setImageUrl('');
      }
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(apiErr.response?.data?.detail || apiErr.message || 'Failed to post to platforms');
    } finally {
      setPosting(false);
    }
  };

  const accountsByPlatform = SUPPORTED_PLATFORMS.map((platform) => {
    const existing = connectedAccounts.find((a) => a.platform === platform);
    return (
      existing ?? {
        platform,
        connected: false,
        needs_reauth: false,
        message: `Not connected to ${platform}`,
      }
    );
  });

  const readyCount = accountsByPlatform.filter((a) => a.connected).length;
  const reauthCount = accountsByPlatform.filter(
    (a) => getConnectionState(a) === 'needs_reauth'
  ).length;
  const connectedPlatforms = accountsByPlatform.filter((a) => a.connected);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Social Media Integrations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Connect LinkedIn, X, and Instagram to publish from one place
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
          onClick={fetchConnectedAccounts}
          disabled={loading}
        >
          Refresh all
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Connection status
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <ChipSummary label={`${readyCount} ready`} color="success" />
          <ChipSummary
            label={`${SUPPORTED_PLATFORMS.length - readyCount - reauthCount} not connected`}
            color="default"
          />
          {reauthCount > 0 && (
            <ChipSummary label={`${reauthCount} need reconnect`} color="error" />
          )}
        </Box>
      </Paper>

      {readyCount > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {readyCount} platform(s) ready to publish
            </Typography>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={() => setPostDialogOpen(true)}
            >
              Post to platforms
            </Button>
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
        {accountsByPlatform.map((account) => {
          const config = platformConfigs[account.platform];
          if (!config) return null;
          return (
            <Grid item xs={12} md={4} key={account.platform}>
              <IntegrationStatusCard
                account={account}
                config={config}
                isConnecting={connecting === account.platform}
                isRefreshing={refreshing === account.platform}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onRefresh={handleRefreshStatus}
              />
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={postDialogOpen} onClose={() => setPostDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Post to social media
            <IconButton onClick={() => setPostDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Post content"
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            sx={{ mt: 1, mb: 3 }}
          />
          <TextField
            fullWidth
            label="Image URL (optional)"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            sx={{ mb: 3 }}
          />
          <Typography variant="subtitle2" gutterBottom>
            Select platforms
          </Typography>
          {connectedPlatforms.map((account) => (
            <FormControlLabel
              key={account.platform}
              control={
                <Checkbox
                  checked={selectedPlatforms.includes(account.platform)}
                  onChange={(e) => {
                    setSelectedPlatforms((prev) =>
                      e.target.checked
                        ? [...prev, account.platform]
                        : prev.filter((p) => p !== account.platform)
                    );
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: platformConfigs[account.platform]?.color,
                    }}
                  >
                    {platformConfigs[account.platform]?.icon}
                  </Avatar>
                  {platformConfigs[account.platform]?.name}
                </Box>
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePost}
            variant="contained"
            disabled={posting || !postContent.trim() || selectedPlatforms.length === 0}
            startIcon={posting ? <CircularProgress size={16} /> : <Send />}
          >
            {posting ? 'Posting...' : 'Post now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

function ChipSummary({
  label,
  color,
}: {
  label: string;
  color: 'success' | 'error' | 'default';
}) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        borderRadius: 1,
        bgcolor:
          color === 'success'
            ? 'success.light'
            : color === 'error'
              ? 'error.light'
              : 'grey.200',
        color:
          color === 'success'
            ? 'success.dark'
            : color === 'error'
              ? 'error.dark'
              : 'text.primary',
        fontWeight: 600,
        fontSize: '0.875rem',
      }}
    >
      {label}
    </Box>
  );
}

export default SocialIntegrationsPage;
